'use client';

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { resetMobileZoom } from '@lib/util/reset-mobile-zoom';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useState } from 'react';

type PriceRangeFilterProps = {
  minPrice?: number;
  maxPrice?: number;
};

const PriceRangeFilter = ({ minPrice, maxPrice }: PriceRangeFilterProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [min, setMin] = useState(minPrice?.toString() ?? '');
  const [max, setMax] = useState(maxPrice?.toString() ?? '');
  const [anchorSide, setAnchorSide] = useState<'bottom start' | 'bottom end'>(
    'bottom start',
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 480px)');
    const update = () =>
      setAnchorSide(mq.matches ? 'bottom end' : 'bottom start');
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const hasActiveFilter = minPrice !== undefined || maxPrice !== undefined;

  const handleApply = (close: () => void) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    resetMobileZoom();
    const params = new URLSearchParams(searchParams);
    if (min) {
      params.set('minPrice', min);
    } else {
      params.delete('minPrice');
    }
    if (max) {
      params.set('maxPrice', max);
    } else {
      params.delete('maxPrice');
    }
    params.set('page', '1');
    router.push(`${pathname}?${params.toString()}`);
    close();
  };

  const handleClear = (close: () => void) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    resetMobileZoom();
    setMin('');
    setMax('');
    const params = new URLSearchParams(searchParams);
    params.delete('minPrice');
    params.delete('maxPrice');
    params.set('page', '1');
    router.push(`${pathname}?${params.toString()}`);
    close();
  };

  return (
    <Popover>
      <PopoverButton
        aria-label={hasActiveFilter ? 'Price filter, 1 active' : 'Price filter'}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-base transition-colors ${
          hasActiveFilter
            ? 'text-fg-interactive-strong border-fg-interactive-strong bg-surface-accent-subtle'
            : 'text-fg-neutral-strong border-border-semantic-subtle bg-white hover:border-border-semantic'
        }`}
      >
        Price
        {hasActiveFilter && (
          <span
            aria-hidden="true"
            className="flex items-center justify-center w-4 h-4 text-[10px] font-bold text-fg-neutral-strong bg-surface-interactive-default rounded-full"
          >
            1
          </span>
        )}
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <PopoverPanel
          anchor={{ to: anchorSide, gap: 4 }}
          className="z-10 w-64 origin-top bg-white border border-border-semantic-subtle rounded-base shadow-lg p-4"
        >
          {({ close }) => (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1">
                  <label
                    htmlFor="price-filter-min"
                    className="block text-xs text-fg-neutral-soft mb-1"
                  >
                    Min
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-fg-neutral-soft">
                      &pound;
                    </span>
                    <input
                      id="price-filter-min"
                      type="number"
                      value={min}
                      onChange={(e) => setMin(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full h-9 pl-7 pr-2 text-base border border-border-semantic-subtle rounded-base bg-white text-fg-neutral-strong focus:outline-hidden focus:border-fg-accent-soft focus:ring-1 focus:ring-fg-accent-soft"
                    />
                  </div>
                </div>
                <span className="text-fg-neutral-soft mt-5">-</span>
                <div className="flex-1">
                  <label
                    htmlFor="price-filter-max"
                    className="block text-xs text-fg-neutral-soft mb-1"
                  >
                    Max
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-fg-neutral-soft">
                      &pound;
                    </span>
                    <input
                      id="price-filter-max"
                      type="number"
                      value={max}
                      onChange={(e) => setMax(e.target.value)}
                      placeholder="Any"
                      min="0"
                      className="w-full h-9 pl-7 pr-2 text-base border border-border-semantic-subtle rounded-base bg-white text-fg-neutral-strong focus:outline-hidden focus:border-fg-accent-soft focus:ring-1 focus:ring-fg-accent-soft"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleApply(close)}
                  className="flex-1 cursor-pointer h-9 text-sm font-medium text-fg-neutral-strong bg-surface-interactive-default rounded-base hover:brightness-95 transition-colors"
                >
                  Apply
                </button>
                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={() => handleClear(close)}
                    className="h-9 px-3 cursor-pointer text-sm font-medium text-fg-neutral-subtle border border-border-semantic-subtle rounded-base hover:text-fg-neutral-strong hover:border-border-semantic transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};

export default PriceRangeFilter;
